"use client";

import { tv, VariantProps } from "tailwind-variants";
import InlineButton from "@/components/ui/inline-button";
import { useState } from "react";
import { FaChevronDown, FaChevronUp } from "react-icons/fa6";

const textCardToggleVariants = tv({
  slots: {
    base: "flex flex-col",
    container: "flex flex-col overflow-hidden text-ellipsis",
    content: "flex flex-col",
    title: "",
    toggle: "",
  },
  variants: {
    variant: {
      mobile: {
        base: "gap-2",
        container: "gap-4",
        title: "font-montserrat text-heading-4 text-primary-600",
        content: "font-roboto text-body-1-400 text-neutral-500 md:!h-fit",
        toggle: "md:hidden",
      },
      small: {
        base: "gap-0",
        container: "gap-2",
        title: "font-montserrat text-subheading-3-500 text-secondary-500",
        content: "font-roboto text-body-1-400 text-neutral-500 md:!h-fit",
        toggle: "md:hidden",
      },
      inquiry: {
        base: "gap-1",
        container: "gap-1",
        title:
          "font-montserrat text-subheading-3-500 text-neutral-500 md:text-subheading-2",
        content: "font-roboto text-body-1-300 text-neutral-400",
        toggle: "ml-auto font-montserrat text-body-1-400 text-secondary-500",
      },
    },
  },
  defaultVariants: {
    variant: "mobile",
  },
});

type TextCardToggleVariant = VariantProps<typeof textCardToggleVariants>;

export interface TextCardToggleProps extends TextCardToggleVariant {
  className?: string;
  containerClassName?: string;
  titleClassName?: string;
  title?: string;
  contentClassName?: string;
  children?: React.ReactNode;
  toggleClassName?: string;
  collapsedHeight?: number;
  defaultCollapsed?: boolean;
}

const TextCardToggle: React.FC<TextCardToggleProps> = ({
  className,
  containerClassName,
  titleClassName,
  contentClassName,
  variant,
  title,
  children,
  toggleClassName,
  collapsedHeight = 48,
  defaultCollapsed = true,
}) => {
  const {
    base,
    container,
    title: titleStyle,
    content,
    toggle,
  } = textCardToggleVariants({ variant });
  const [isCollapsed, setCollapsed] = useState(defaultCollapsed);

  const handleToggle = () => {
    setCollapsed((prev) => !prev);
  };
  return (
    <div
      className={base({
        className: className,
      })}
    >
      <div
        className={container({
          className: containerClassName,
        })}
      >
        <h2
          className={titleStyle({
            className: titleClassName,
          })}
        >
          {title}
        </h2>
        <div
          style={{
            height: isCollapsed ? `${collapsedHeight}px` : "max-content",
          }}
          className={content({
            className: contentClassName,
          })}
        >
          {children}
        </div>
      </div>
      <InlineButton
        onClick={handleToggle}
        aria-expanded={isCollapsed ? "true" : "false"}
        color={variant === "small" ? "orange" : "teal"}
        size="md"
        className={toggle({
          className: toggleClassName,
        })}
      >
        {isCollapsed ? "Read more" : "Show less"}
        {variant === "inquiry" && (
          <span>
            {isCollapsed ? (
              <FaChevronDown data-testid="chevron-down" />
            ) : (
              <FaChevronUp data-testid="chevron-up" />
            )}
          </span>
        )}
      </InlineButton>
    </div>
  );
};

export default TextCardToggle;
